import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import SelectOptionCard, {
  SelectOptionCardProps,
} from '@/components/molecules/select-option-card/select-option-card';
import Calendar from '@/components/organisms/calendar/calendar';
import variables from '@/components/organisms/search-bar/search-bar.variables';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import getLocale from '@/libraries/getters/get-locale';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import SearchBarSectionMobileWrapper from '../search-bar-section-mobile-wrapper/search-bar-section-mobile-wrapper';
import styles from './search-bar-date-dropdown.module.scss';

export interface SearchBarDateDropdownProps {
  /** A list of available dates */
  availableDates: Date[];
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Default number of adults per room */
  description?: string | string[];
  /** The travel length options */
  durationOptions?: SelectOptionCardProps[];
  /** Indicates whether the dropdown is in a loading state */
  isLoading?: boolean;
  /** Indicates whether the dropdown is visible */
  isVisible?: boolean;
  /** Indicates whether the dropdown is used in the search widget */
  isWidget?: boolean;
  /** The label to show in the mobile wrapper */
  label: string;
  /** Maximum date to select a range from */
  maxDate?: Date;
  /** Minimum date to select a range from */
  minDate?: Date;
  /** An event that is triggered on change */
  onChange?: (dates: Date[]) => void;
  /** An event that is triggered on click duration option*/
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on click close */
  onClickClose?: () => void;
  /** An event that is triggered on click next */
  onClickNext?: () => void;
  /** An event that is triggered on click search */
  onClickSearch?: () => void;
  /** Indicates whether the user selects a date range */
  rangeSelect?: boolean;
  /** Number of days that represents the length of the stay */
  rangeSize?: number;
  /** Preselected date in the calendar */
  selectedDate?: Date;
  /** Preselected date range end in the calendar */
  selectedMaxDate?: Date;
}

const SearchBarDateDropdown = memo(
  forwardRef(
    (
      {
        availableDates,
        className,
        customRangeStyle = false,
        description,
        durationOptions = [],
        isLoading = false,
        isVisible = false,
        isWidget = false,
        maxDate,
        minDate,
        label,
        onChange,
        onChangeDuration,
        onClickBack,
        onClickClose,
        onClickNext,
        onClickSearch,
        rangeSelect,
        rangeSize,
        selectedDate,
        selectedMaxDate,
      }: SearchBarDateDropdownProps,
      ref,
    ) => {
      const { campaignAvailableDates } = useSearchContext();
      const isLaptop = useWindowSize('laptop');
      const locale = getLocale();
      const dateSectionBanner = variables?.dateSectionBanner;

      const { t } = useI18n();

      const [localSelectedDate, setLocalSelectedDate] = useState<Date | null>(
        selectedDate ?? null,
      );

      const [localSelectedMaxDate, setLocalSelectedMaxDate] =
        useState<Date | null>(selectedMaxDate ?? null);

      const handleChangeDate = useCallback(
        (dates: Date[]) => {
          setLocalSelectedDate(dates[0]);
          setLocalSelectedMaxDate(dates[1]);
          onChange?.(dates);
        },
        [onChange],
      );

      const handleClickClear = useCallback(() => {
        setLocalSelectedDate(null);
        setLocalSelectedMaxDate(null);
        onChange?.([]);
      }, [onChange]);

      useEffect(() => {
        setLocalSelectedDate(selectedDate === undefined ? null : selectedDate);
        setLocalSelectedMaxDate(
          selectedMaxDate === undefined ? null : selectedMaxDate,
        );
      }, [selectedDate, selectedMaxDate]);

      useImperativeHandle(ref, () => ({ clear: handleClickClear }), [
        handleClickClear,
      ]);

      const renderedOptions = useMemo(
        () =>
          durationOptions.map(option => (
            <SelectOptionCard
              key={option.id}
              headline={option.headline}
              icon={option.icon}
              isSelected={option.isSelected}
              onClick={() => onChangeDuration?.(option.id as string)}
            />
          )),
        [durationOptions, onChangeDuration],
      );

      if (isLaptop && !isVisible) {
        return null;
      }

      const warningMessage = () => {
        if (!dateSectionBanner || !('text' in dateSectionBanner)) return null;

        return (
          <AlertBanner
            className={styles.warningContainer}
            text={(dateSectionBanner as { text: string }).text}
          />
        );
      };

      return (
        <div
          className={classNames(styles.component, className, {
            [styles.isWidget]: isWidget,
          })}
        >
          {isLaptop ? (
            <motion.div
              animate={isVisible ? 'show' : 'hidden'}
              className={classNames(styles.desktopWrapper, {
                [styles.isVisible]: isVisible,
              })}
              exit="hidden"
              initial="hidden"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              variants={{
                hidden: {
                  opacity: 0,
                  transitionEnd: {
                    display: 'none',
                  },
                  translateY: 0,
                },
                show: {
                  display: 'block',
                  opacity: 1,
                  translateY: 0,
                },
              }}
            >
              <div className={styles.content}>
                {isWidget && (
                  <div className={styles.label}>
                    <Typography tag="label" tagStyle="labelSmall">
                      {t('items.search.searchBar.date.chooseDate')}
                    </Typography>
                  </div>
                )}

                <div className={styles.dropdownWrapper}>
                  {durationOptions.length > 0 && (
                    <React.Fragment>
                      <div className={styles.durationOptions}>
                        <Typography
                          className={styles.label}
                          tag="label"
                          tagStyle="labelMedium"
                        >
                          {t('items.search.searchBar.date.duration')}:
                        </Typography>

                        {renderedOptions}
                      </div>

                      <div className={styles.divider} />
                    </React.Fragment>
                  )}

                  <Calendar
                    customRangeStyle={customRangeStyle}
                    displayedMonths={2}
                    highlightedDates={availableDates}
                    isLoading={isLoading && isVisible}
                    locale={locale}
                    maxDate={maxDate}
                    minDate={minDate}
                    offers={campaignAvailableDates}
                    onChange={handleChangeDate}
                    rangeSelect={rangeSelect}
                    rangeSize={rangeSize}
                    selectedDate={localSelectedDate}
                    selectedMaxDate={localSelectedMaxDate}
                  />

                  {dateSectionBanner && warningMessage()}

                  {!isWidget && (
                    <div className={styles.footer}>
                      <Link onClick={handleClickClear}>
                        <Typography
                          className={styles.link}
                          tagStyle="bodyMedium"
                        >
                          {t('items.common.words.clear')}
                        </Typography>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ) : (
            <SearchBarSectionMobileWrapper
              buttonLabel={t('items.common.words.search')}
              description={description}
              isOpen={isVisible}
              label={label}
              onClickBack={onClickBack}
              onClickClear={handleClickClear}
              onClickClose={onClickClose}
              onClickNext={onClickNext}
              onClickSearch={onClickSearch}
              shouldTriggerSearch
              showBackButton
            >
              <div className={styles.mobileWrapper}>
                {durationOptions.length > 0 && (
                  <div className={styles.durationOptionsWrapper}>
                    <Typography
                      className={styles.label}
                      tag="label"
                      tagStyle="labelMedium"
                    >
                      {t('items.search.searchBar.date.duration')}:
                    </Typography>

                    <div className={styles.durationOptions}>
                      {renderedOptions}
                    </div>
                  </div>
                )}

                <Calendar
                  customRangeStyle={customRangeStyle}
                  displayedMonths={1}
                  highlightedDates={availableDates}
                  isLoading={isLoading && isVisible}
                  locale={locale}
                  maxDate={maxDate}
                  minDate={minDate}
                  onChange={handleChangeDate}
                  rangeSelect={rangeSelect}
                  rangeSize={rangeSize}
                  selectedDate={localSelectedDate}
                  selectedMaxDate={localSelectedMaxDate}
                />

                {dateSectionBanner && warningMessage()}
              </div>
            </SearchBarSectionMobileWrapper>
          )}
        </div>
      );
    },
  ),
);

SearchBarDateDropdown.displayName = 'SearchBarDateDropdown';

export default SearchBarDateDropdown;
